import React from 'react';
import {Box} from '@mui/material';
import {AlertBanner} from '@resurs-internal/mui';
import {isRestError} from '@resurs-internal/rb-app-api';
import type {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {faEnvelope, faMobileScreen} from '@fortawesome/pro-light-svg-icons';
import {useTranslate} from '../../../hooks';
import type {VerifyContactFlow} from '../useVerifyContactFlow';
import type {ContactChannel} from '../VerifyContactDialogView';
import {VerifyContactStepViewHeader} from './VerifyContactStepViewHeader';
import {EmailField} from './EmailField';
import {PhoneField} from './PhoneField';

export interface VerifyContactStepViewInputProps {
  flow: VerifyContactFlow;

  intro?: {title: string; subtitle: string};
}

const DEFAULT_HEADER_KEYS = {
  verify: {
    email: {
      title: 'verify-contact.email.title',
      subtitle: 'verify-contact.email.description',
    },
    phone: {
      title: 'verify-contact.phone.title',
      subtitle: 'verify-contact.phone.description',
    },
  },
  add: {
    email: {
      title: 'verify-contact.add.email.title',
      subtitle: 'verify-contact.add.email.description',
    },
    phone: {
      title: 'verify-contact.add.phone.title',
      subtitle: 'verify-contact.add.phone.description',
    },
  },
  edit: {
    email: {
      title: 'verify-contact.edit.email.title',
      subtitle: 'verify-contact.edit.email.description',
    },
    phone: {
      title: 'verify-contact.edit.phone.title',
      subtitle: 'verify-contact.edit.phone.description',
    },
  },
} as const;

const CHANNEL_ICON: Record<ContactChannel, IconDefinition> = {
  email: faEnvelope,
  phone: faMobileScreen,
};

export const VerifyContactStepViewInput: React.FC<
  VerifyContactStepViewInputProps
> = ({flow, intro}) => {
  const {channel, mode, sendError} = flow;
  const translate = useTranslate();

  const defaults = DEFAULT_HEADER_KEYS[mode][channel];
  const header = intro ?? {
    title: translate(defaults.title),
    subtitle: translate(defaults.subtitle),
  };

  return (
    <Box>
      <VerifyContactStepViewHeader icon={CHANNEL_ICON[channel]} {...header} />

      {channel === 'email' ? (
        <EmailField flow={flow} />
      ) : (
        <PhoneField flow={flow} />
      )}

      {sendError && (
        <Box sx={{mt: 1.5}}>
          <AlertBanner
            severity={'error'}
            title={translate(
              isRestError(sendError) && sendError.status === 409
                ? 'verify-contact.email.conflict'
                : 'click.to.pay.verify.send-failed',
            )}
          />
        </Box>
      )}
    </Box>
  );
};
